import { useAuthRedux } from '@/hooks/use-auth-redux';
import { useCloudFunction } from '@/hooks/use-cloud-function';
import { useData } from '@/hooks/use-data';
import { useIntegration } from '@/hooks/use-integration';
import { useIntegrationPartnerEndpoint } from '@/hooks/use-integration-partner-endpoint';
import { envConfig } from '@/lib/env-config';
import { IntegrationTokenManager } from '@/lib/integration-token-manager';
import { AuthStateSelector } from '@/store/selectors';
import type { UserLocation } from '@/store/slices/dataSlice';
import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

/**
 * AuthenticateReloadHandler Component
 *
 * This component ensures that the authenticate cloud function is called
 * on every page reload to get fresh access tokens for integration partners.
 *
 * The authenticate cloud function uses:
 * - secretId: from integration partner's cloud_secret_id
 * - username: from user's username
 * - url: from the authenticate endpoint URL (from Redux store)
 *
 * After successful authentication, it calls the "get-user-locations" endpoint
 * to fetch user locations and stores them in Redux.
 *
 * Note: Integration endpoints are persisted in Redux store, so we don't need to fetch them again.
 */
export function AuthenticateReloadHandler() {
  const { user, isAuthenticated, loading } = useAuthRedux();
  const { callFunction } = useCloudFunction();
  const { endpoints, partner, loading: integrationLoading } = useIntegration();
  const { callEndpoint } = useIntegrationPartnerEndpoint();
  const {
    userLocationsFetched,
    setUserLocations,
    setUserLocationsLoading,
    setUserLocationsError,
  } = useData();
  const integrationToken = useSelector(AuthStateSelector.integrationToken);
  // Ref to ensure we only fetch locations once per token
  const hasFetchedLocations = useRef(false);

  const hasCalledAuthenticate = useRef(false);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryCount = useRef(0);
  const maxRetries = 10;
  const hasInitialized = useRef(false);

  // Main authentication logic
  useEffect(() => {
    // Skip if already initialized or still loading
    if (hasInitialized.current || loading || integrationLoading) {
      return;
    }

    // Skip if not authenticated or no user
    if (!isAuthenticated || !user) {
      return;
    }

    // Skip if user doesn't have integration partner
    if (!user.ev_integration_partner_id) {
      return;
    }

    // Skip if already called authenticate
    if (hasCalledAuthenticate.current) {
      return;
    }

    // Check if we have all required data
    if (!partner?.cloud_secret_id || !user.username || endpoints.length === 0) {
      // Retry after a delay if we don't have the data yet
      if (retryCount.current < maxRetries) {
        retryTimeoutRef.current = setTimeout(() => {
          retryCount.current++;
          hasInitialized.current = false; // Reset to try again
        }, 1000 * retryCount.current); // Exponential backoff
      } else {
        console.error(
          '❌ AuthenticateReloadHandler: Max retries reached, giving up'
        );
      }
      return;
    }

    // Mark as initialized to prevent multiple calls
    hasInitialized.current = true;
    hasCalledAuthenticate.current = true;

    // Find authenticate endpoint
    const authenticateEndpoint = endpoints.find(
      (ep) => ep.action === 'authenticate' && ep.environment === envConfig.env
    );

    if (!authenticateEndpoint) {
      console.error(
        '❌ AuthenticateReloadHandler: No authenticate endpoint found'
      );
      return;
    }

    // Call the cloud function
    callFunction('authenticate', {
      secretId: partner.cloud_secret_id,
      username: user.username,
      url: authenticateEndpoint.endpoint_url,
    })
      .then((result) => {
        if (result.success && result.data) {
          let tokenResponse: any = result.data;

          // Type guard: check if it's already a valid object
          if (
            typeof tokenResponse === 'object' &&
            tokenResponse &&
            typeof tokenResponse.token === 'string' &&
            typeof tokenResponse.expiresIn === 'string' &&
            !isNaN(parseInt(tokenResponse.expiresIn))
          ) {
            IntegrationTokenManager.storeToken(tokenResponse, partner.id);
            return;
          }

          // If not, and it's a string, try to parse it
          if (typeof tokenResponse === 'string') {
            try {
              const parsed = JSON.parse(tokenResponse);
              if (
                parsed &&
                typeof parsed.token === 'string' &&
                typeof parsed.expiresIn === 'string' &&
                !isNaN(parseInt(parsed.expiresIn))
              ) {
                IntegrationTokenManager.storeToken(parsed, partner.id);
                return;
              } else {
                console.error(
                  '[AuthenticateReloadHandler] ❌ Parsed tokenResponse is invalid:',
                  parsed
                );
              }
            } catch (e) {
              console.error(
                '[AuthenticateReloadHandler] ❌ Failed to parse tokenResponse string:',
                tokenResponse,
                e
              );
            }
          }

          // If all else fails
          console.error(
            '[AuthenticateReloadHandler] ❌ Invalid token response from cloud function:',
            result.data
          );
          return;
        } else {
          console.error(
            '[AuthenticateReloadHandler] ❌ Cloud function failed:',
            result.error
          );
        }
      })
      .catch((error) => {
        console.error(
          '❌ AuthenticateReloadHandler: Error calling cloud function:',
          error
        );
      });

    // Cleanup function
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [
    isAuthenticated,
    user,
    partner,
    endpoints,
    loading,
    integrationLoading,
    callFunction,
    callEndpoint,
    userLocationsFetched,
    setUserLocations,
    setUserLocationsLoading,
    setUserLocationsError,
  ]);

  // Function to call get-user-locations endpoint after authentication
  const callUserLocationsEndpoint = useCallback(async () => {
    // Skip if locations have already been fetched
    if (userLocationsFetched) {
      return;
    }

    // Debug: Check token status before making the call
    const tokenInfo = IntegrationTokenManager.getTokenInfo();

    // If token is not valid, wait a bit and retry (in case Redux state hasn't propagated)
    if (!tokenInfo.isValid) {
      setTimeout(() => {
        const retryTokenInfo = IntegrationTokenManager.getTokenInfo();
        if (retryTokenInfo.isValid) {
          callUserLocationsEndpoint();
        } else {
          console.error('❌ Token still not valid after retry');
          setUserLocationsError('Failed to get valid integration token');
        }
      }, 200);
      return;
    }

    try {
      setUserLocationsLoading(true);
      setUserLocationsError('');

      const result = await callEndpoint(
        'get-user-locations',
        undefined,
        undefined
      );

      if (result.success && result.data) {
        // Process simple array response
        const rawLocations = result.data as unknown as Array<{
          locationId: number;
          locationName: string;
        }>;

        if (Array.isArray(rawLocations)) {
          // Convert locationId from number to string and store locations in Redux
          const locations: UserLocation[] = rawLocations.map((location) => ({
            locationId: String(location.locationId),
            locationName: location.locationName,
          }));
          setUserLocations(locations);
        } else {
          console.error(
            '❌ AuthenticateReloadHandler: Invalid response structure - expected array:',
            rawLocations
          );
          setUserLocationsError(
            'Invalid response structure from get-user-locations endpoint'
          );
        }
      } else {
        console.warn(
          '⚠️ AuthenticateReloadHandler: get-user-locations call failed:',
          result.error
        );
        setUserLocationsError(result.error || 'Failed to fetch user locations');
      }
    } catch (error) {
      console.error(
        '❌ AuthenticateReloadHandler: Error calling get-user-locations:',
        error
      );
      setUserLocationsError(
        error instanceof Error ? error.message : 'Unknown error'
      );
    } finally {
      setUserLocationsLoading(false);
    }
  }, [
    callEndpoint,
    userLocationsFetched,
    setUserLocations,
    setUserLocationsLoading,
    setUserLocationsError,
  ]);

  // Reset ref when token changes
  useEffect(() => {
    hasFetchedLocations.current = false;
  }, [integrationToken?.token]);

  // New effect: call get-user-locations when integrationToken becomes valid
  useEffect(() => {
    if (
      integrationToken &&
      integrationToken.token &&
      integrationToken.expiresAt > Date.now() &&
      !userLocationsFetched &&
      !hasFetchedLocations.current
    ) {
      hasFetchedLocations.current = true;
      callUserLocationsEndpoint();
    }
  }, [integrationToken, userLocationsFetched, callUserLocationsEndpoint]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  return null; // This component doesn't render anything
}
